import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDesaPoliticaFitxers,
  usePoliticaFitxers,
  type PoliticaFitxersDto,
} from '@/api/administracio';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { Carregant, ErrorCarrega } from '@/pages/EstatsPagina';
import { useMe } from '@/permisos/MeContext';
import { BarraCanvis } from './marc/canvis';
import { CapPanell, ErrorDesa } from './comu';
import './administracio.css';
import './sistema.css';

const FORMATS = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'png', 'jpg', 'zip'];
const REBUTJATS = 'XLSM, DOCM, EXE, MSI, BAT, PS1, JS';
const CONTROLS = ['antimalware', 'formatReal', 'macros', 'executables', 'marcaAigua'] as const;

/** Nombre de camps que difereixen de la política desada. */
function canvis(a: PoliticaFitxersDto, b: PoliticaFitxersDto | undefined): number {
  if (!b) return 0;
  const formats =
    a.formats.length !== b.formats.length || a.formats.some((f) => !b.formats.includes(f));
  return (
    Number(formats) +
    Number(a.midaMaxMb !== b.midaMaxMb) +
    Number(a.maxDocuments !== b.maxDocuments)
  );
}

export function W35PoliticaFitxers() {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const { pot } = useMe();
  const politica = usePoliticaFitxers();
  const desa = useDesaPoliticaFitxers();
  const [edicio, setP] = useState<PoliticaFitxersDto | null>(null);
  const editable = pot('cai.documents.politica', 'MODIFICAR');
  const desada = politica.data?.data;
  const p = edicio ?? desada ?? null;

  if (politica.isError) return <ErrorCarrega onReintenta={() => void politica.refetch()} />;
  if (!p) return <Carregant />;

  const commuta = (fmt: string) =>
    setP({
      ...p,
      formats: p.formats.includes(fmt) ? p.formats.filter((x) => x !== fmt) : [...p.formats, fmt],
    });
  const midaInvalida = !Number.isFinite(p.midaMaxMb) || p.midaMaxMb < 1 || p.midaMaxMb > 500;
  const maxInvalid =
    !Number.isInteger(p.maxDocuments) || p.maxDocuments < 1 || p.maxDocuments > 1000;
  const valid = p.formats.length > 0 && !midaInvalida && !maxInvalid;
  const n = canvis(p, desada);
  const numero = (v: string) => (v.trim() === '' ? Number.NaN : Number(v));

  return (
    <section className="pantalla" data-screen="W-35">
      <Toolbar
        titol={t('politica.titol')}
        enrere="/admin/documentacio"
        subtitol={
          p.updatedAt ? t('politica.darrerCanvi', { data: f.data(p.updatedAt) }) : undefined
        }
      />
      <div className="content stack sis-estret">
        <ErrorDesa
          error={desa.error}
          onRecarrega={() => {
            setP(null);
            desa.reset();
            void politica.refetch();
          }}
        />
        {desa.isSuccess && n === 0 ? (
          <p className="info-banda" role="status">
            <Icona nom="checkmark-circle" />
            {t('comu.desat')}
          </p>
        ) : null}
        <div className="panel">
          <CapPanell titol={t('politica.formats')} icona="doc-text" colorIcona="c-blue" />
          <div className="panel__body form">
            <p className="camp__ajuda" style={{ margin: 0 }}>
              {t('sistema.politica.ajudaFormats')}
            </p>
            <div className="chips">
              {FORMATS.map((fmt) => (
                <button
                  key={fmt}
                  type="button"
                  className="chip-canal"
                  aria-pressed={p.formats.includes(fmt)}
                  disabled={!editable}
                  onClick={() => commuta(fmt)}
                >
                  <Icona nom={p.formats.includes(fmt) ? 'checkmark' : 'plus'} />
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
            {p.formats.length === 0 ? (
              <p className="error-text" role="alert">
                {t('politica.capFormat')}
              </p>
            ) : null}
          </div>
        </div>
        <div className="panel">
          <CapPanell titol={t('politica.limits')} icona="sliders" colorIcona="c-blue" />
          <div className="panel__body grid grid-2" style={{ gap: 16 }}>
            <label className="camp">
              <span className="camp__label">{t('politica.midaMax')}</span>
              <input
                className="input"
                type="number"
                min={1}
                max={500}
                value={Number.isNaN(p.midaMaxMb) ? '' : p.midaMaxMb}
                disabled={!editable}
                aria-invalid={midaInvalida}
                aria-describedby="pol-mida-err"
                onChange={(e) => setP({ ...p, midaMaxMb: numero(e.target.value) })}
              />
              {midaInvalida ? (
                <span id="pol-mida-err" className="error-text">
                  {t('sistema.politica.midaInvalida')}
                </span>
              ) : null}
            </label>
            <label className="camp">
              <span className="camp__label">{t('politica.maxDocuments')}</span>
              <input
                className="input"
                type="number"
                min={1}
                max={1000}
                step={1}
                value={Number.isNaN(p.maxDocuments) ? '' : p.maxDocuments}
                disabled={!editable}
                aria-invalid={maxInvalid}
                aria-describedby="pol-max-err"
                onChange={(e) => setP({ ...p, maxDocuments: numero(e.target.value) })}
              />
              {maxInvalid ? (
                <span id="pol-max-err" className="error-text">
                  {t('sistema.politica.maxInvalid')}
                </span>
              ) : null}
            </label>
          </div>
        </div>
        <details className="sis-detalls">
          <summary>
            <Icona nom="shield-check" className="c-green" />
            {t('sistema.politica.sempre')}
          </summary>
          <ul className="sis-sempre">
            {CONTROLS.map((c) => (
              <li key={c}>
                <strong>{t(`politica.controls.${c}.titol`)}</strong>
                <span className="dim"> · {t(`politica.controls.${c}.text`)}</span>
              </li>
            ))}
            <li>
              <strong>{t('politica.rebutjats')}</strong>
              <span className="dim"> · {REBUTJATS}</span>
            </li>
            <li>
              <strong>{t('sistema.politica.noDisponibles')}</strong>
              <span className="dim"> · {t('politica.bloqueig')}</span>
            </li>
            <li>
              <strong>{t('politica.conservacio')}</strong>
              <span className="dim"> · {t('politica.historial')}</span>
            </li>
          </ul>
        </details>
      </div>
      {editable ? (
        <BarraCanvis
          n={n}
          nom={t('politica.titol')}
          desant={desa.isPending}
          desactivat={!valid}
          onDescarta={() => {
            setP(null);
            desa.reset();
          }}
          onDesa={() =>
            desa.mutate(
              { politica: p, etag: politica.data?.etag ?? null },
              { onSuccess: () => setP(null) },
            )
          }
        >
          {!valid ? t('sistema.comu.revisaErrors') : null}
        </BarraCanvis>
      ) : null}
    </section>
  );
}
